import { IssueCaseStatusCode, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { endOfDay, startOfDay, startOfToday, subDays } from 'date-fns';
import { chunk } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { combineLatest, map, of, switchMap } from 'rxjs';

import { ISSUE_CASE_ACTIVE_STATUSES } from 'src/constants/issue.constants';
import { CrmUnifiedRowData, processUnifiedCrmData } from 'src/utils/crm/crm-data-util';
import { formatDate } from 'src/utils/date-util';
import { WHERE } from 'src/utils/firebase/firebase-manager';
import { observeIssueCases, observeIssueTasks } from 'src/utils/firebase/firebase-short-cut';

/** Firestore `in` 쿼리 최대 인자 수 */
const FIRESTORE_IN_CHUNK_SIZE = 30;

export const useCrmBoardData = () => {
  const [dateRange, setDateRange] = useState<[Date, Date]>([subDays(startOfToday(), 7), startOfToday()]);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [issueStatuses, setIssueStatuses] = useState<IssueCaseStatusCode[]>(ISSUE_CASE_ACTIVE_STATUSES);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rawData, setRawData] = useState<CrmUnifiedRowData[]>([]);

  const rowData = useMemo(() => {
    if (issueStatuses.length === 0) return rawData;
    return rawData.filter((item) => issueStatuses.includes(item.status as IssueCaseStatusCode));
  }, [rawData, issueStatuses]);

  useEffect(() => {
    if (!storeId) {
      setRawData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const [startDate, endDate] = dateRange;
    const formattedStartDate = formatDate(startOfDay(startDate), "yyyy-MM-dd'T'HH:mm:ss+0900");
    const formattedEndDate = formatDate(endOfDay(endDate), "yyyy-MM-dd'T'HH:mm:ss+0900");

    const issueWhere: WHERE[] = [
      ['storeId', '==', storeId],
      ['registeredAt', '>=', formattedStartDate],
      ['registeredAt', '<=', formattedEndDate],
    ];

    const queryOptions = { sortKey: 'registeredAt', orderBy: 'desc' as const };

    const subscription = observeIssueCases(issueWhere, queryOptions)
      .pipe(
        switchMap((issues) => {
          const caseIds = issues.map((c) => c._id);
          if (caseIds.length === 0) {
            return of({ issues, tasks: [] as IssueTaskDoc[] });
          }
          const taskObservables = chunk(caseIds, FIRESTORE_IN_CHUNK_SIZE).map((ids) =>
            observeIssueTasks([['issueCaseId', 'in', ids]]),
          );
          return combineLatest(taskObservables).pipe(map((taskArrays) => ({ issues, tasks: taskArrays.flat() })));
        }),
        map(({ issues, tasks }) => processUnifiedCrmData(issues, tasks)),
      )
      .subscribe({
        next: (rows) => {
          setRawData(rows);
          setIsLoading(false);
        },
        error: (err) => {
          console.error('Failed to fetch CRM data', err);
          setIsLoading(false);
        },
      });

    return () => subscription.unsubscribe();
  }, [dateRange, storeId]);

  return {
    dateRange,
    setDateRange,
    storeId,
    setStoreId,
    issueStatuses,
    setIssueStatuses,
    rowData,
    isLoading,
  };
};
